import React, { useState, useEffect } from "react";
import {
  Users,
  CreditCard,
  TrendingUp,
  AlertCircle,
  CheckCircle,
  XCircle,
  PauseCircle,
  Search,
  Filter,
  Download,
  RefreshCw,
  Tag,
  DollarSign,
  Calendar,
  Mail,
  MoreVertical,
  ArrowUpDown
} from "lucide-react";

function AdminDashboard() {
  const [activeTab, setActiveTab] = useState("dashboard");
  const [stats, setStats] = useState({
    total_subscriptions: 0,
    active_subscriptions: 0,
    trial_subscriptions: 0,
    cancelled_subscriptions: 0,
    total_revenue: 0,
    discount_codes_used: 0
  });
  const [subscriptions, setSubscriptions] = useState([]);
  const [discountCodes, setDiscountCodes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  // Cargar datos al montar el componente
  useEffect(() => {
    loadDashboardData();
    loadSubscriptions();
    loadDiscountCodes();
  }, []);

  const loadDashboardData = async () => {
    try {
      // Simular llamada a API
      const mockStats = {
        total_subscriptions: 127,
        active_subscriptions: 89,
        trial_subscriptions: 23,
        cancelled_subscriptions: 15,
        total_revenue: 890000,
        discount_codes_used: 34
      };
      setStats(mockStats);
    } catch (error) {
      console.error('Error loading dashboard data:', error);
    }
  };

  const loadSubscriptions = async () => {
    try {
      setLoading(true);
      // Simular datos de suscripciones
      const mockSubscriptions = [
        {
          id: "sub_001",
          payer_email: "empresa1@constructora.com",
          status: "authorized",
          amount: 10000,
          original_amount: 10000,
          trial_days: 14,
          discount_code: null,
          metadata: { empresa: "Constructora ABC", constructora_id: "const_001" },
          created_at: "2024-01-15T10:30:00Z",
          updated_at: "2024-01-15T10:30:00Z"
        },
        {
          id: "sub_002",
          payer_email: "empresa2@construcciones.com",
          status: "pending",
          amount: 5000,
          original_amount: 10000,
          trial_days: 14,
          discount_code: "CONSTRUCTORA50",
          metadata: { empresa: "Construcciones XYZ", constructora_id: "const_002" },
          created_at: "2024-01-14T15:45:00Z",
          updated_at: "2024-01-14T15:45:00Z"
        },
        {
          id: "sub_003",
          payer_email: "info@obraslimitadas.com",
          status: "cancelled",
          amount: 10000,
          original_amount: 10000,
          trial_days: 14,
          discount_code: null,
          metadata: { empresa: "Obras Limitadas", constructora_id: "const_003" },
          created_at: "2024-01-13T09:20:00Z",
          updated_at: "2024-01-16T11:10:00Z"
        }
      ];
      setSubscriptions(mockSubscriptions);
    } catch (error) {
      console.error('Error loading subscriptions:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadDiscountCodes = async () => {
    try {
      // Simular códigos de descuento
      const mockCodes = [
        {
          id: 1,
          code: 'GRATIS14DIAS',
          type: 'free',
          value: 100,
          description: 'Plan gratuito por 14 días',
          max_uses: 100,
          current_uses: 23,
          expires_at: '2024-12-31',
          active: true
        },
        {
          id: 2,
          code: 'CONSTRUCTORA50',
          type: 'percentage',
          value: 50,
          description: '50% de descuento',
          max_uses: 50,
          current_uses: 8,
          expires_at: '2024-12-31',
          active: true
        },
        {
          id: 3,
          code: 'DESCUENTO2000',
          type: 'fixed_amount',
          value: 2000,
          description: '$2.000 de descuento',
          max_uses: 30,
          current_uses: 3,
          expires_at: '2024-12-31',
          active: true
        }
      ];
      setDiscountCodes(mockCodes);
    } catch (error) {
      console.error('Error loading discount codes:', error);
    }
  };

  const handleRefund = async (subscriptionId, amount) => {
    if (window.confirm(`¿Estás seguro de que querés reembolsar $${amount.toLocaleString()}?`)) {
      try {
        setLoading(true);
        // Aquí harías la llamada real a tu API
        console.log('Processing refund for subscription:', subscriptionId);
        alert('Reembolso procesado exitosamente');
        loadSubscriptions(); // Recargar datos
      } catch (error) {
        console.error('Error processing refund:', error);
        alert('Error al procesar el reembolso');
      } finally {
        setLoading(false);
      }
    }
  };

  const handlePauseSubscription = async (subscriptionId) => {
    if (window.confirm('¿Querés pausar esta suscripción?')) {
      try {
        setLoading(true);
        console.log('Pausing subscription:', subscriptionId);
        alert('Suscripción pausada exitosamente');
        loadSubscriptions();
      } catch (error) {
        console.error('Error pausing subscription:', error);
        alert('Error al pausar la suscripción');
      } finally {
        setLoading(false);
      }
    }
  };

  const handleCancelSubscription = async (subscriptionId) => {
    if (window.confirm('¿Estás seguro de que querés cancelar esta suscripción?')) {
      try {
        setLoading(true);
        console.log('Cancelling subscription:', subscriptionId);
        alert('Suscripción cancelada exitosamente');
        loadSubscriptions();
      } catch (error) {
        console.error('Error cancelling subscription:', error);
        alert('Error al cancelar la suscripción');
      } finally {
        setLoading(false);
      }
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'authorized':
        return <CheckCircle size={16} className="text-green-500" />;
      case 'pending':
        return <AlertCircle size={16} className="text-yellow-500" />;
      case 'cancelled':
        return <XCircle size={16} className="text-red-500" />;
      case 'paused':
        return <PauseCircle size={16} className="text-blue-500" />;
      default:
        return <AlertCircle size={16} className="text-gray-500" />;
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'authorized':
        return 'Activa';
      case 'pending':
        return 'Trial';
      case 'cancelled':
        return 'Cancelada';
      case 'paused':
        return 'Pausada';
      default:
        return status;
    }
  };

  const filteredSubscriptions = subscriptions.filter(sub => {
    const matchesSearch = sub.payer_email.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         sub.metadata.empresa.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || sub.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const renderDashboard = () => (
    <div className="space-y-6">
      {/* Estadísticas principales */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm">
          <div className="flex items-center">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Users className="h-6 w-6 text-blue-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total Suscripciones</p>
              <p className="text-2xl font-bold text-gray-900">{stats.total_subscriptions}</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm">
          <div className="flex items-center">
            <div className="p-2 bg-green-100 rounded-lg">
              <CheckCircle className="h-6 w-6 text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Activas</p>
              <p className="text-2xl font-bold text-gray-900">{stats.active_subscriptions}</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm">
          <div className="flex items-center">
            <div className="p-2 bg-yellow-100 rounded-lg">
              <AlertCircle className="h-6 w-6 text-yellow-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">En Trial</p>
              <p className="text-2xl font-bold text-gray-900">{stats.trial_subscriptions}</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm">
          <div className="flex items-center">
            <div className="p-2 bg-emerald-100 rounded-lg">
              <DollarSign className="h-6 w-6 text-emerald-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Ingresos Totales</p>
              <p className="text-2xl font-bold text-gray-900">${stats.total_revenue.toLocaleString()}</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm">
          <div className="flex items-center">
            <div className="p-2 bg-purple-100 rounded-lg">
              <Tag className="h-6 w-6 text-purple-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Códigos Usados</p>
              <p className="text-2xl font-bold text-gray-900">{stats.discount_codes_used}</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm">
          <div className="flex items-center">
            <div className="p-2 bg-red-100 rounded-lg">
              <XCircle className="h-6 w-6 text-red-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Canceladas</p>
              <p className="text-2xl font-bold text-gray-900">{stats.cancelled_subscriptions}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Suscripciones recientes */}
      <div className="bg-white rounded-lg border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">Suscripciones Recientes</h3>
        </div>
        <div className="divide-y divide-gray-200">
          {subscriptions.slice(0, 5).map((sub) => (
            <div key={sub.id} className="px-6 py-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  {getStatusIcon(sub.status)}
                  <div>
                    <p className="text-sm font-medium text-gray-900">
                      {sub.metadata.empresa}
                    </p>
                    <p className="text-sm text-gray-500">{sub.payer_email}</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-sm font-medium text-gray-900">
                    ${sub.amount.toLocaleString()}
                  </p>
                  <p className="text-sm text-gray-500">
                    {new Date(sub.created_at).toLocaleDateString()}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderSubscriptions = () => (
    <div className="space-y-6">
      {/* Filtros y búsqueda */}
      <div className="bg-white p-4 rounded-lg border border-gray-200">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
              <input
                type="text"
                placeholder="Buscar por email o empresa..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>
          <div className="flex gap-2">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="all">Todos los estados</option>
              <option value="authorized">Activas</option>
              <option value="pending">En trial</option>
              <option value="paused">Pausadas</option>
              <option value="cancelled">Canceladas</option>
            </select>
            <button
              onClick={loadSubscriptions}
              disabled={loading}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
            >
              <RefreshCw size={16} className={loading ? "animate-spin" : ""} />
              Actualizar
            </button>
          </div>
        </div>
      </div>

      {/* Tabla de suscripciones */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Empresa
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Estado
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Monto
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Descuento
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Fecha
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Acciones
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredSubscriptions.map((sub) => (
                <tr key={sub.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <div className="text-sm font-medium text-gray-900">
                        {sub.metadata.empresa}
                      </div>
                      <div className="text-sm text-gray-500">{sub.payer_email}</div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      {getStatusIcon(sub.status)}
                      <span className="text-sm text-gray-900">
                        {getStatusText(sub.status)}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      ${sub.amount.toLocaleString()}
                    </div>
                    {sub.original_amount !== sub.amount && (
                      <div className="text-xs text-gray-500 line-through">
                        ${sub.original_amount.toLocaleString()}
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {sub.discount_code ? (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        {sub.discount_code}
                      </span>
                    ) : (
                      <span className="text-sm text-gray-400">-</span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {new Date(sub.created_at).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex items-center justify-end gap-2">
                      {sub.status === 'authorized' && (
                        <>
                          <button
                            onClick={() => handlePauseSubscription(sub.id)}
                            className="text-blue-600 hover:text-blue-900 text-sm"
                          >
                            Pausar
                          </button>
                          <button
                            onClick={() => handleRefund(sub.id, sub.amount)}
                            className="text-yellow-600 hover:text-yellow-900 text-sm"
                          >
                            Reembolsar
                          </button>
                        </>
                      )}
                      {sub.status !== 'cancelled' && (
                        <button
                          onClick={() => handleCancelSubscription(sub.id)}
                          className="text-red-600 hover:text-red-900 text-sm"
                        >
                          Cancelar
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );

  const renderDiscountCodes = () => (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold text-gray-900">Códigos de Descuento</h2>
        <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
          Crear Código
        </button>
      </div>

      <div className="grid gap-4">
        {discountCodes.map((code) => (
          <div key={code.id} className="bg-white p-6 rounded-lg border border-gray-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <Tag className="h-5 w-5 text-purple-600" />
                </div>
                <div>
                  <h3 className="text-lg font-medium text-gray-900">{code.code}</h3>
                  <p className="text-sm text-gray-500">{code.description}</p>
                </div>
              </div>
              <div className="text-right">
                <div className="text-sm text-gray-500">
                  {code.current_uses} / {code.max_uses} usos
                </div>
                <div className="text-sm text-gray-500">
                  Vence: {code.expires_at}
                </div>
              </div>
            </div>
            <div className="mt-4 pt-4 border-t border-gray-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <span className="text-sm font-medium text-gray-700">
                    {code.type === 'percentage' && `${code.value}% descuento`}
                    {code.type === 'fixed_amount' && `$${code.value.toLocaleString()} descuento`}
                    {code.type === 'free' && 'Gratuito'}
                  </span>
                </div>
                <div className="w-full max-w-xs">
                  <div className="bg-gray-200 rounded-full h-2">
                    <div 
                      className="bg-purple-600 h-2 rounded-full"
                      style={{ width: `${(code.current_uses / code.max_uses) * 100}%` }}
                    ></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <div style={{ minHeight: "100vh", background: "#f8fafc", fontFamily: "Inter, sans-serif" }}>
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div className="flex items-center gap-4">
              <img src="https://llohruvkcdcyolmwlivx.supabase.co/storage/v1/object/public/public-assets//logonokk.png" alt="NOKK" className="h-8 w-auto" />
              <h1 className="text-2xl font-bold text-gray-900">Panel de Administración</h1>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-500">Admin</span>
              <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                <span className="text-sm font-medium text-blue-600">A</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex space-x-8">
            <button
              onClick={() => setActiveTab("dashboard")}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === "dashboard"
                  ? "border-blue-500 text-blue-600"
                  : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
              }`}
            >
              Dashboard
            </button>
            <button
              onClick={() => setActiveTab("subscriptions")}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === "subscriptions"
                  ? "border-blue-500 text-blue-600"
                  : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
              }`}
            >
              Suscripciones
            </button>
            <button
              onClick={() => setActiveTab("discounts")}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === "discounts"
                  ? "border-blue-500 text-blue-600"
                  : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
              }`}
            >
              Códigos de Descuento
            </button>
          </nav>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {activeTab === "dashboard" && renderDashboard()}
        {activeTab === "subscriptions" && renderSubscriptions()}
        {activeTab === "discounts" && renderDiscountCodes()}
      </div>
    </div>
  );
}

export default AdminDashboard; 